import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { analyticsAPI, subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, Loader2, DollarSign, Calendar, TrendingUp, Truck, Wrench,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

export default function MaintenanceForecast() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [forecast, setForecast] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subRes, forecastRes] = await Promise.all([
        subscriptionAPI.status(),
        analyticsAPI.forecast().catch(() => null),
      ]);
      setIsPro(subRes.data.is_pro);
      if (forecastRes) setForecast(forecastRes.data);
    } catch {
      toast.error("Failed to load forecast data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <Crown className="w-12 h-12 text-amber-500" />
        <h2 className="text-xl font-bold">Pro Feature</h2>
        <p className="text-muted-foreground text-center">Maintenance cost forecast requires a Pro subscription.</p>
        <Button onClick={() => navigate("/subscription")}>Upgrade to Pro</Button>
      </div>
    );
  }

  const monthlyData = (forecast?.monthly_forecast || []).map((m) => ({
    ...m,
    label: new Date(m.month + "-01T00:00:00").toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
  }));

  const upcomingServices = [];
  (forecast?.monthly_forecast || []).forEach((m) => {
    (m.services || []).forEach((s) => upcomingServices.push(s));
  });
  upcomingServices.sort((a, b) => a.due_date.localeCompare(b.due_date));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/analytics")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Maintenance Forecast
            </h1>
            <p className="text-xs text-muted-foreground">Projected costs for the next 12 months</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-1 text-green-500" />
              <p className="text-2xl font-bold">${(forecast?.total_projected || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Projected (12mo)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Wrench className="w-8 h-8 mx-auto mb-1 text-blue-500" />
              <p className="text-2xl font-bold">{upcomingServices.length}</p>
              <p className="text-xs text-muted-foreground">Upcoming Services</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Truck className="w-8 h-8 mx-auto mb-1 text-primary" />
              <p className="text-2xl font-bold">{forecast?.vehicles?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Vehicles Tracked</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Cost Chart */}
        {monthlyData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Monthly Projected Costs</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#999" }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      formatter={(value) => [`$${value.toLocaleString()}`, "Projected Cost"]}
                      contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8 }}
                      labelStyle={{ color: "#999" }}
                    />
                    <Bar dataKey="projected_cost" radius={[4, 4, 0, 0]}>
                      {monthlyData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.projected_cost > 0 ? "#f97316" : "#333"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Services Timeline */}
        {upcomingServices.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Upcoming Service Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {upcomingServices.slice(0, 20).map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-center min-w-[60px]">
                      <p className="font-bold">
                        {new Date(s.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "short" })}
                      </p>
                      <p className="text-muted-foreground">
                        {new Date(s.due_date + "T00:00:00").toLocaleDateString("en-US", { day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.vehicle} — {s.category}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono">
                    ${s.projected_cost.toLocaleString()}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Per-Vehicle Breakdown */}
        {(forecast?.vehicles || []).filter((v) => v.upcoming.length > 0).map((v) => (
          <Card key={v.vehicle_id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Truck className="w-4 h-4" />
                {v.nickname}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {v.upcoming.slice(0, 8).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-border last:border-0">
                  <div>
                    <span className="font-medium">{s.title}</span>
                    <span className="text-muted-foreground ml-2">{s.category}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">
                      {new Date(s.due_date + "T00:00:00").toLocaleDateString()}
                    </span>
                    <span className="font-mono font-bold">${s.projected_cost}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Empty state */}
        {upcomingServices.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No forecast data yet</p>
            <p className="text-sm">Add service records with intervals to generate cost projections.</p>
          </div>
        )}
      </main>
    </div>
  );
}
